import { legacyCsvTable } from './legacyImport'

export const AI_CHUNK_ROWS = 8
export const AI_CHUNK_CHARS = 4000

const clean = value => String(value ?? '').trim()

export function prepareAiRows(source, { format = 'auto' } = {}) {
  const text = clean(source)
  if (!text) return []
  const firstLine = text.split(/\r?\n/, 1)[0]
  if (format === 'table' || (format === 'auto' && (firstLine.includes(',') || firstLine.includes('\t')))) {
    const table = legacyCsvTable(text)
    if (table.headers.length > 1 && table.rows.length) {
      return table.rows.map((values, index) => {
        const used = new Set()
        const source_fields = {}
        for (let column = 0; column < Math.max(table.headers.length, values.length); column++) {
          const label = clean(table.rawHeaders?.[column]) || `unnamed_${column + 1}`
          const key = used.has(label) ? `${label}_column_${column + 1}` : label
          used.add(key)
          source_fields[key] = clean(values[column])
        }
        return { row_number: table.rowNumbers?.[index] || index + 2, source_fields }
      })
    }
  }
  const lines = text.split(/\r?\n/)
  if (format === 'text_blocks') {
    const blocks = []
    let block = []
    let start = 2
    lines.forEach((line, index) => {
      if (clean(line)) {
        if (!block.length) start = index + 2
        block.push(line)
      } else if (block.length) {
        blocks.push({ row_number: start, source_fields: { text: block.join('\n') } })
        block = []
      }
    })
    if (block.length) blocks.push({ row_number: start, source_fields: { text: block.join('\n') } })
    return blocks
  }
  return lines.map((line, index) => ({ row_number: index + 2, source_fields: { text: line } }))
    .filter(row => clean(row.source_fields.text))
}

export function chunkAiRows(rows) {
  const chunks = []
  let chunk = []
  for (const row of rows) {
    const size = JSON.stringify(row).length
    if (size > AI_CHUNK_CHARS) throw new Error(`Source row ${row.row_number} is too large for AI analysis.`)
    if (chunk.length && (chunk.length >= AI_CHUNK_ROWS || JSON.stringify([...chunk, row]).length > AI_CHUNK_CHARS)) {
      chunks.push(chunk)
      chunk = []
    }
    chunk.push(row)
  }
  if (chunk.length) chunks.push(chunk)
  return chunks
}

export function mergeAiChunks(chunks, replies) {
  const expected = chunks.flat().map(row => row.row_number)
  const proposed = replies.flatMap(reply => reply?.rows || [])
  const numbers = proposed.map(row => row.row_number)
  if (replies.some(reply => !reply) || proposed.length !== expected.length
    || new Set(numbers).size !== numbers.length || expected.some(number => !numbers.includes(number))) {
    throw new Error('AI did not return every source row exactly once. Nothing is ready for approval.')
  }
  const byNumber = new Map(proposed.map(row => [row.row_number, row]))
  return chunks.flat().map(sourceRow => ({ ...sourceRow, ...byNumber.get(sourceRow.row_number) }))
}

export function chunkReplyMatchesSource(chunk, reply) {
  const expected = chunk.map(row => row.row_number)
  const actual = reply?.rows?.map(row => row.row_number) || []
  return actual.length === expected.length && new Set(actual).size === actual.length
    && expected.every(number => actual.includes(number))
}

export async function readAiChunkResponse(res) {
  if (!res.ok) throw new Error(res.status === 504
    ? 'Analysis timed out. Retry this chunk; no records were imported.'
    : 'Analysis failed. Retry this chunk; no records were imported.')
  if (!res.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    throw new Error('Analysis returned a non-JSON response. Retry this chunk; no records were imported.')
  }
  try {
    const body = await res.json()
    if (!Array.isArray(body?.rows)) throw new Error()
    return body
  } catch {
    throw new Error('Analysis returned an invalid response. Retry this chunk; no records were imported.')
  }
}

export function proposalCsv(columns, rows) {
  const escape = value => `"${String(value ?? '').replace(/"/g, '""')}"`
  return [columns.join(','), ...rows.map(row => columns.map(column => escape(row.proposed?.[column])).join(','))].join('\n')
}
